import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import type { DetectedLink, LinkType } from '../link-detector.tsx';
import {
	LAST_FLAG_SHOWN_TIMESTAMP_KEY,
	STORAGE_3P_FLAG_SHOWN_ELIGIBILITY_KEY,
} from '../traits-constants.tsx';
import { TIME_WINDOWS } from '../environment-config.tsx';
import type { ThirdPartyInviteType } from '../traits-helpers.tsx';

/**
 * LAYER 2: Check if global 15-minute throttle is active
 *
* Uses Confluence storage-manager localStorage instance so the underlying
* implementation (BSC vs window.localStorage) is abstracted away.

 */
export const checkGlobalThrottle = (): boolean => {
	try {
		const lastShownStr = localStorage.getItem(LAST_FLAG_SHOWN_TIMESTAMP_KEY);

		if (!lastShownStr) {
			return true; // No previous flag shown, eligible
		}

		const lastShown = parseInt(lastShownStr, 10);
		const timeSince = Date.now() - lastShown;

		if (timeSince < TIME_WINDOWS.GLOBAL_THROTTLE) {
			return false;
		}

		return true;
	} catch (e) {
		return true; // Allow on error
	}
};

/**
 * Update the global throttle timestamp
 */
export const updateGlobalThrottle = (timestamp: number): void => {
	try {
		localStorage.setItem(LAST_FLAG_SHOWN_TIMESTAMP_KEY, timestamp.toString());
	} catch (e) {
		// Silent fail
	}
};

/**
 * Per-type dismiss state stored in localStorage for the 3P-within-1P invite spotlight.
 * Values:
 *   undefined = never read from storage (default)
 *   0 = initial (never dismissed)
 *   1 = soft dismissed once
 *   2 = soft dismissed again
 *   3 = permanently dismissed (dontShowAgain)
 */
export type DismissState3pIn1p = {
	slack: number | undefined;
	mailto: number | undefined;
};

/**
 * Read the current per-type dismiss state from localStorage.
 * Returns undefined for each type if no value is stored (e.g. first-time user).
 * The stored JSON format mirrors the Jira implementation:
 *   { slack: { state: number, timestamp: number }, mailto: { state: number, timestamp: number } }
 */
export const check3pIn1pDismissState = (): DismissState3pIn1p => {
	const defaultResponse: DismissState3pIn1p = { slack: undefined, mailto: undefined };
	try {
		const state = localStorage.getItem(STORAGE_3P_FLAG_SHOWN_ELIGIBILITY_KEY);
		if (!state) {
			return defaultResponse;
		}
		const parsedState = JSON.parse(state);
		if (typeof parsedState === 'object' && parsedState !== null) {
			const slackVal = parsedState.slack?.state;
			const mailtoVal = parsedState.mailto?.state;
			return {
				slack: Number(slackVal) || undefined,
				mailto: Number(mailtoVal) || undefined,
			};
		}
		return defaultResponse;
	} catch {
		return defaultResponse;
	}
};

/**
 * Write the updated dismiss state for a single invite type to localStorage.
 * Only the dismissed type's state and timestamp are updated; the other type's
 * existing values are preserved exactly as stored to avoid corrupting its timestamp.
 */
export const set3pIn1pDismissState = (
	shownAtTimestamp: number,
	type: ThirdPartyInviteType,
	dismissType: number,
): void => {
	try {
		// Read the raw stored object so we can preserve the other type's timestamp
		// independently. Using check3pIn1pDismissState() would lose the timestamps.
		let existing: Record<string, { state: number | undefined; timestamp: number | undefined }> = {};
		try {
			const raw = localStorage.getItem(STORAGE_3P_FLAG_SHOWN_ELIGIBILITY_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (typeof parsed === 'object' && parsed !== null) {
					existing = parsed;
				}
			}
		} catch {
			// If we can't read existing, we'll just write the new entry
		}

		localStorage.setItem(
			STORAGE_3P_FLAG_SHOWN_ELIGIBILITY_KEY,
			JSON.stringify({
				...existing,
				// Only overwrite the entry for the type that was just dismissed
				[type]: { state: dismissType, timestamp: shownAtTimestamp },
			}),
		);
	} catch {
		// No-op: storage failures should not block spotlight display
	}
};

/**
 * LAYER 4: Select single type from eligible types
 * Primary: First pasted link wins
 * Fallback: Static priority (microsoft > slack)
 */
export const selectSingleType = (
	eligibleTypes: LinkType[],
	newLinks: DetectedLink[],
): LinkType | null => {
	if (eligibleTypes.length === 0) return null;
	if (eligibleTypes.length === 1) return eligibleTypes[0];

	// Primary: First pasted link type wins
	for (const link of newLinks) {
		if (eligibleTypes.includes(link.type)) {
			return link.type;
		}
	}

	// Fallback: Priority order
	const priority: LinkType[] = ['microsoft', 'slack'];
	for (const type of priority) {
		if (eligibleTypes.includes(type)) {
			return type;
		}
	}

	return eligibleTypes[0];
};
